import { useEffect } from "react";
import { SITE, DEFAULT_TITLE } from "./useCanonical";
import { setMeta, setJsonLd } from "./usePageMeta";
import { categoryPath } from "./categories";

const ORG_ID = "https://thetealist.com/#org";

function postSeoFields(post) {
  const coverAbsolute = post.cover_image?.startsWith("http")
    ? post.cover_image
    : `${window.location.origin}${post.cover_image}`;
  return {
    title: post.meta_title || `${post.title} — The Tea List`,
    description: post.meta_description || post.excerpt,
    postUrl: `${SITE}/blog/${post.slug}`,
    coverAbsolute,
    authorName: post.author_name || "The Tea List",
  };
}

function applyMetaTags(post, { title, description, postUrl, coverAbsolute, authorName }) {
  document.title = title;
  const tags = {
    'meta[name="description"]': description,
    'meta[name="robots"]': "index, follow, max-image-preview:large",
    'meta[property="og:title"]': title,
    'meta[property="og:description"]': description,
    'meta[property="og:type"]': "article",
    'meta[property="og:url"]': postUrl,
    'meta[property="og:site_name"]': "The Tea List",
    'meta[property="og:locale"]': "en_AU",
    'meta[property="og:image"]': coverAbsolute,
    'meta[property="og:image:secure_url"]': coverAbsolute,
    'meta[property="og:image:alt"]': post.title,
    'meta[property="article:published_time"]': post.created_at || "",
    'meta[property="article:modified_time"]': post.updated_at || post.created_at || "",
    'meta[property="article:author"]': authorName,
    'meta[property="article:section"]': post.category || "",
    'meta[name="twitter:card"]': "summary_large_image",
    'meta[name="twitter:title"]': title,
    'meta[name="twitter:description"]': description,
    'meta[name="twitter:image"]': coverAbsolute,
    'meta[name="twitter:image:alt"]': post.title,
  };
  Object.entries(tags).forEach(([selector, value]) => setMeta(selector, value));
}

function authorEntity(authorName) {
  let authorId = null;
  if (/amina/i.test(authorName)) authorId = "#amina";
  else if (/sameera/i.test(authorName)) authorId = "#sameera";
  if (!authorId) return { "@type": "Organization", "@id": ORG_ID, name: "The Tea List" };
  return { "@type": "Person", "@id": `https://thetealist.com/${authorId}`, name: authorName, url: `${SITE}/about` };
}

function articleJsonLd(post, { description, postUrl, coverAbsolute, authorName }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${postUrl}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": postUrl },
    url: postUrl,
    headline: post.title,
    description,
    image: [coverAbsolute],
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: authorEntity(authorName),
    publisher: { "@id": ORG_ID },
    isPartOf: { "@id": "https://thetealist.com/#website" },
    articleSection: post.category,
    keywords: (post.tags || []).join(", "),
    wordCount: (post.content || "").trim().split(/\s+/).length,
    timeRequired: `PT${post.read_time || 5}M`,
    inLanguage: "en-AU",
    isAccessibleForFree: true,
  };
}

function breadcrumbJsonLd(post, postUrl) {
  const item = (position, name, url) => ({ "@type": "ListItem", position, name, item: url });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      item(1, "Home", `${SITE}/`),
      item(2, "Journal", `${SITE}/blog`),
      item(3, post.category, `${SITE}${categoryPath(post.category)}`),
      item(4, post.title, postUrl),
    ],
  };
}

/** Article/Breadcrumb JSON-LD, OG/Twitter tags and robots for a post page. */
export default function usePostSeo(data, loading) {
  useEffect(() => {
    if (!data?.post) {
      if (!loading && !data) setMeta('meta[name="robots"]', "noindex, follow");
      return () => setMeta('meta[name="robots"]', "index, follow, max-image-preview:large");
    }
    const { post } = data;
    const fields = postSeoFields(post);
    applyMetaTags(post, fields);
    setJsonLd("ld-article", articleJsonLd(post, fields));
    setJsonLd("ld-breadcrumb", breadcrumbJsonLd(post, fields.postUrl));
    return () => {
      document.title = DEFAULT_TITLE;
      setJsonLd("ld-article", null);
      setJsonLd("ld-breadcrumb", null);
    };
  }, [data, loading]);
}
